"use client";

import Link from "@/infrastructure/router/Link";
import { useRouter, useSearchParams } from "@/infrastructure/router/navigation";
import type { Route } from "@/infrastructure/router/types";
import { useEffect, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import { artistPublicTrackURL } from "@/domain/auth/AuthProviderConfiguration";
import { browserDiscoveryRepository } from "@/domain/discovery/DiscoveryRepository";
import {
	listLocalTrackAssets,
	type LocalTrackAsset,
} from "@/domain/media/LocalTrackAssetStore";
import type { DiscoveryTrack } from "@/domain/discovery/DiscoveryRepository";
import { useTranslation } from "@/i18n/ArtistI18n";

interface LocalPlayerMedia {
	readonly kind: "local";
	readonly asset: LocalTrackAsset;
	readonly audioURL: string;
	readonly artworkURL: string | null;
}

interface PublicPlayerMedia {
	readonly kind: "public";
	readonly track: DiscoveryTrack;
}

type PlayerMedia = LocalPlayerMedia | PublicPlayerMedia;

type PlayerQueueItem =
	| { readonly kind: "local"; readonly id: string; readonly title: string }
	| { readonly kind: "public"; readonly id: string; readonly title: string };

export function GenerationPlayer({
	publicTrackId: routePublicTrackId,
}: Readonly<{ publicTrackId?: string }> = {}) {
	const auth = useArtistSession();
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	const { t } = useTranslation();
	const router = useRouter();
	const search = useSearchParams();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const localTrackId = search.get("id") ?? search.get("request");
	const publicTrackId = routePublicTrackId ?? search.get("remote");
	const [media, setMedia] = useState<PlayerMedia | null | undefined>(undefined);
	const [queue, setQueue] = useState<readonly PlayerQueueItem[]>([]);
	const [shareStatus, setShareStatus] = useState<string | null>(null);

	useEffect(() => {
		let active = true;
		let audioURL: string | null = null;
		let artworkURL: string | null = null;
		if (publicTrackId !== null) {
			Promise.all([
				browserDiscoveryRepository.loadPublicTrack(publicTrackId),
				browserDiscoveryRepository.loadPublicQueue(),
			])
				.then(([track, tracks]) => {
					if (!active) return;
					if (track === null || !track.playbackURL) {
						setMedia(null);
						setQueue([]);
						return;
					}
					setMedia({ kind: "public", track });
					setQueue(
						tracks
							.filter((item) => Boolean(item.playbackURL))
							.map((item) => ({ kind: "public", id: item.id, title: item.title })),
					);
				})
				.catch(() => {
					if (active) setMedia(null);
				});
			return () => {
				active = false;
			};
		}
		if (auth.phase !== "user" || auth.scope === null || localTrackId === null) {
			setMedia(null);
			setQueue([]);
			return;
		}
		const scope = auth.scope;
		listLocalTrackAssets(scope)
			.then((assets) => {
				if (!active) return;
				setQueue(
					assets.map((asset) => ({ kind: "local", id: asset.id, title: asset.title })),
				);
				const asset = assets.find((candidate) => candidate.id === localTrackId) ?? null;
				if (asset === null) {
					setMedia(null);
					return;
				}
				audioURL = URL.createObjectURL(asset.audioBlob);
				artworkURL =
					asset.artworkBlob === null
						? null
						: URL.createObjectURL(asset.artworkBlob);
				setMedia({ kind: "local", asset, audioURL, artworkURL });
			})
			.catch(() => {
				if (active) setMedia(null);
			});
		return () => {
			active = false;
			if (audioURL !== null) URL.revokeObjectURL(audioURL);
			if (artworkURL !== null) URL.revokeObjectURL(artworkURL);
		};
	}, [auth.phase, auth.scope, localTrackId, publicTrackId]);

	const currentId =
		media == null ? null : media.kind === "local" ? media.asset.id : media.track.id;
	const currentIndex =
		currentId === null ? -1 : queue.findIndex((item) => item.id === currentId);
	const previous = currentIndex > 0 ? queue[currentIndex - 1] : undefined;
	const next =
		currentIndex >= 0 && currentIndex < queue.length - 1
			? queue[currentIndex + 1]
				: undefined;

	const sharePublicTrack = async () => {
		if (media?.kind !== "public") return;
		const url = artistPublicTrackURL(runtimeConfiguration, media.track.id);
		try {
			if (navigator.share !== undefined) {
				await navigator.share({ title: media.track.title, text: t("player.shareText", { title: media.track.title, artist: media.track.artist }), url });
				setShareStatus(t("player.shared"));
			} else {
				await navigator.clipboard.writeText(url);
				setShareStatus(t("player.linkCopied"));
			}
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") return;
			setShareStatus(t("player.shareFailed"));
		}
	};

	useEffect(() => {
		if (media == null || typeof navigator === "undefined" || !("mediaSession" in navigator)) {
			return;
		}
		const session = navigator.mediaSession;
		const artworkURL =
			media.kind === "local" ? media.artworkURL : media.track.artworkURL;
		session.metadata = new MediaMetadata({
			title:
				media.kind === "local"
					? media.asset.title || media.asset.audioFilename
					: media.track.title,
			artist: media.kind === "local" ? "Artist" : media.track.artist,
			album: "Artist",
			artwork: artworkURL == null ? [] : [{ src: artworkURL }],
		});

		const setHandler = (
			action: MediaSessionAction,
			handler: MediaSessionActionHandler | null,
		) => {
			try {
				session.setActionHandler(action, handler);
			} catch {
				// Browsers expose Media Session actions incrementally. Unsupported
				// actions stay unavailable without disabling the supported controls.
			}
		};
		const seekBy = (seconds: number) => {
			const audio = audioRef.current;
			if (audio === null || !Number.isFinite(audio.duration)) return;
			audio.currentTime = Math.min(
				Math.max(audio.currentTime + seconds, 0),
				audio.duration,
			);
		};

		setHandler("play", () => {
			void audioRef.current?.play();
		});
		setHandler("pause", () => {
			audioRef.current?.pause();
		});
		setHandler("stop", () => {
			const audio = audioRef.current;
			if (audio === null) return;
			audio.pause();
			audio.currentTime = 0;
			session.playbackState = "none";
		});
		setHandler("seekbackward", (details) => {
			seekBy(-(details.seekOffset ?? 10));
		});
		setHandler("seekforward", (details) => {
			seekBy(details.seekOffset ?? 10);
		});
		setHandler("seekto", (details) => {
			const audio = audioRef.current;
			if (audio === null || details.seekTime === undefined) return;
			audio.currentTime = details.seekTime;
		});
		setHandler(
			"previoustrack",
			previous === undefined ? null : () => router.push(playerHref(previous)),
		);
		setHandler(
			"nexttrack",
			next === undefined ? null : () => router.push(playerHref(next)),
		);

		return () => {
			for (const action of [
				"play",
				"pause",
				"stop",
				"seekbackward",
				"seekforward",
				"seekto",
				"previoustrack",
				"nexttrack",
			] satisfies readonly MediaSessionAction[]) {
				setHandler(action, null);
			}
			session.metadata = null;
			session.playbackState = "none";
		};
	}, [media, next, previous, router]);

	if (media === undefined) {
		return (
			<section className="player-proof-gate">
					<p role="status">{t("player.loading")}</p>
			</section>
		);
	}
	if (media === null) {
		return (
			<section className="player-proof-gate">
					<h1>{t("player.missingTitle")}</h1>
					<p>{t("player.missingMessage")}</p>
					<Link className="primary-action" href="/make">
						{t("player.backToMake")}
				</Link>
			</section>
		);
	}

	return (
		<section className="shared-player">
			<div className="shared-player__art">
				{(media.kind === "local" ? media.artworkURL : media.track.artworkURL) == null ? (
					<span aria-hidden="true">♪</span>
				) : (
					// biome-ignore lint/performance/noImgElement: playback supports browser-local Blob URLs and approved remote HTTPS artwork.
					<img
						src={media.kind === "local" ? media.artworkURL ?? undefined : media.track.artworkURL}
						alt=""
					/>
				)}
			</div>
			<div className="shared-player__details">
					<p className="eyebrow">{t("player.nowPlaying")}</p>
				<h1>
					{media.kind === "local"
						? media.asset.title || media.asset.audioFilename
						: media.track.title}
				</h1>
				<p>{media.kind === "local" ? media.asset.audioFilename : media.track.artist}</p>
				<audio
					ref={audioRef}
					controls
					preload="metadata"
					src={media.kind === "local" ? media.audioURL : media.track.playbackURL}
					onPlay={() => {
						setMediaSessionPlaybackState("playing");
						if (auth.scope !== null) {
							void browserDiscoveryRepository.recordRecent(
								media.kind === "local" ? media.asset.id : media.track.id,
								auth.scope,
							);
						}
					}}
					onPause={() => setMediaSessionPlaybackState("paused")}
					onLoadedMetadata={(event) => updateMediaSessionPosition(event.currentTarget)}
					onDurationChange={(event) => updateMediaSessionPosition(event.currentTarget)}
					onRateChange={(event) => updateMediaSessionPosition(event.currentTarget)}
					onTimeUpdate={(event) => updateMediaSessionPosition(event.currentTarget)}
					onEnded={() => {
						if (next !== undefined) {
							router.push(playerHref(next));
						} else {
							setMediaSessionPlaybackState("none");
						}
					}}
				>
					<track kind="captions" />
				</audio>
				<div className="player-controls">
					{previous === undefined ? (
							<button type="button" disabled aria-label={t("player.previous")}>
							◀
						</button>
					) : (
							<Link href={playerHref(previous)} aria-label={t("player.previous")}>
							◀
						</Link>
					)}
					{next === undefined ? (
							<button type="button" disabled aria-label={t("player.next")}>
							▶
						</button>
					) : (
							<Link href={playerHref(next)} aria-label={t("player.next")}>
							▶
						</Link>
					)}
				</div>
				<p className="player-queue-position">
					{t("player.queuePosition", { current: Math.max(currentIndex + 1, 1), total: Math.max(queue.length, 1) })}
				</p>
				{media.kind === "public" ? (
					<button type="button" className="secondary-action" onClick={() => void sharePublicTrack()}>
						{t("player.share")}
					</button>
				) : null}
				{shareStatus === null ? null : <p role="status">{shareStatus}</p>}
				{media.kind === "local" ? (
					<Link href={`/track/edit?id=${encodeURIComponent(media.asset.id)}`}>{t("player.edit")}</Link>
				) : (
					<Link href="/discover">{t("player.backToDiscover")}</Link>
				)}
			</div>
		</section>
	);
}

function playerHref(item: PlayerQueueItem): Route {
	const key = item.kind === "local" ? "id" : "remote";
	return `/player?${key}=${encodeURIComponent(item.id)}` as Route;
}

function setMediaSessionPlaybackState(state: MediaSessionPlaybackState): void {
	if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
	navigator.mediaSession.playbackState = state;
}

function updateMediaSessionPosition(audio: HTMLAudioElement): void {
	if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
	if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
	try {
		navigator.mediaSession.setPositionState({
			duration: audio.duration,
			playbackRate: audio.playbackRate,
			position: Math.min(Math.max(audio.currentTime, 0), audio.duration),
		});
	} catch {
		// Ignore transient metadata states while the browser swaps queue items.
	}
}
